import { X, Phone, Shield, Store, Heart, Copy, Check, Tag, Share2, MessageCircle, ExternalLink, RectangleHorizontal, Car, Building2, MapPin, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { toast } from 'sonner';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PriceDisplay } from './PriceDisplay';
import PlateVisual from './plates/PlateVisual';
import { getDisplayNumber, getDisplayPlateNumber, isNumberMasked } from '../utils/digitMask';
import StoreBadges from './StoreBadges';
import PriceOrContact from './PriceOrContact';
import { useOperatorView } from '../hooks/usePhoneOperators';
import OperatorLogo from './OperatorLogo';
import PhoneNumberLockup from './PhoneNumberLockup';

// Emirates colors for car plates
const EMIRATES_COLORS = {
  dubai: '#1E9BD9',
  abudhabi: '#E4002B',
  sharjah: '#00A651',
  ajman: '#F7941D',
  rak: '#8B5CF6',
  fujairah: '#EC4899',
  uaq: '#14B8A6'
};

const getItemType = (item) => {
  if (item?.item_type) return item.item_type;
  if (item?.store_type === 'ecommerce') return 'ecommerce';
  if (item?.store_type === 'car_plates' || item?.plateNumber) return 'car_plate';
  if (item?.store_type === 'auto' || item?.make) return 'car';
  if (item?.store_type === 'real_estate' || item?.property_type) return 'real_estate';
  return 'phone_number';
};

export default function ItemDetailsModal({ item, isOpen, onClose, onFavoriteToggle, isFavorite }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [copied, setCopied] = useState(false);
  const [carImageIndex, setCarImageIndex] = useState(0);
  const op = useOperatorView(item);

  if (!isOpen || !item) return null;

  const itemType = getItemType(item);
  const isSold = item.status === 'sold';
  const emirateColor = EMIRATES_COLORS[item.emirate?.toLowerCase()] || '#1E9BD9';

  const handleCopyNumber = () => {
    if (isNumberMasked(item)) {
      toast.info(isAr ? 'الرقم مخفي جزئياً — لا يمكن نسخه' : 'Partially masked — copy disabled');
      return;
    }
    const textToCopy = itemType === 'car_plate' 
      ? `${item.plateCode || ''} ${getDisplayPlateNumber(item)}` 
      : getDisplayNumber(item);
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success(isAr ? 'تم النسخ' : 'Copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    let text;
    if (itemType === 'car_plate') {
      const p = getDisplayPlateNumber(item);
      text = isAr 
        ? `لوحة مميزة ${item.plateCode} ${p} - ${item.emirate} - ${item.price} درهم`
        : `Premium plate ${item.plateCode} ${p} - ${item.emirate} - ${item.price} AED`;
    } else if (itemType === 'car') {
      text = isAr 
        ? `${item.make} ${item.model} - ${item.price} درهم`
        : `${item.make} ${item.model} - ${item.price} AED`;
    } else {
      const n = getDisplayNumber(item);
      text = isAr 
        ? `رقم مميز ${n} - ${item.price} درهم`
        : `Premium number ${n} - ${item.price} AED`;
    }
    
    if (navigator.share) {
      navigator.share({ title: 'CloudGM', text, url: window.location.href });
    } else {
      navigator.clipboard.writeText(text);
      toast.success(isAr ? 'تم نسخ المعلومات' : 'Info copied');
    }
  };

  const handleWhatsApp = () => {
    let message;
    if (itemType === 'car_plate') {
      const p = getDisplayPlateNumber(item);
      message = isAr 
        ? `مرحباً، أنا مهتم باللوحة ${item.plateCode} ${p} - ${item.emirate} المعروضة بسعر ${item.price} درهم`
        : `Hi, I'm interested in the plate ${item.plateCode} ${p} - ${item.emirate} listed at ${item.price} AED`;
    } else if (itemType === 'car') {
      message = isAr 
        ? `مرحباً، أنا مهتم بالسيارة ${item.make} ${item.model} المعروضة بسعر ${item.price} درهم`
        : `Hi, I'm interested in ${item.make} ${item.model} listed at ${item.price} AED`;
    } else {
      const n = getDisplayNumber(item);
      message = isAr 
        ? `مرحباً، أنا مهتم بالرقم ${n} المعروض بسعر ${item.price} درهم`
        : `Hi, I'm interested in the number ${n} listed at ${item.price} AED`;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // E-commerce Product Modal
  if (itemType === 'ecommerce') {
    const productImage = item.images?.[0] || item.image;
    const isValidProductImage = productImage && (productImage.startsWith('http') || productImage.startsWith('data:') || productImage.startsWith('/'));
    const productName = isAr ? (item.name || item.nameEn) : (item.nameEn || item.name);
    const storeName = item.store?.name || item.store_name;
    const storeVerified = item.store?.verified || item.store_verified;
    
    return (
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'}`}
          onClick={(e) => e.stopPropagation()}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Product Image */}
          <div className={`relative aspect-square ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-100'}`}>
            {isValidProductImage ? (
              <img 
                src={productImage} 
                alt={productName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className={`h-16 w-16 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'}`} />
              </div>
            )}
            
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-3 end-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Discount Badge */}
            {item.hasDiscount && item.discountPercentage && (
              <div className="absolute top-3 start-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1.5 rounded-full">
                -{item.discountPercentage}%
              </div>
            )}
            
            {/* Verified Badge */}
            {storeVerified && (
              <div className="absolute bottom-3 end-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {isAr ? 'متجر موثق' : 'Verified'}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-5">
            {/* Product Name */}
            <h2 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {productName}
            </h2>
            
            {/* Price */}
            <div className="flex items-center gap-3 mb-4">
              {item.hasDiscount && item.salePrice ? (
                <>
                  <span className="text-2xl font-bold text-red-500">
                    <PriceDisplay price={item.salePrice} />
                  </span>
                  <span className={`line-through text-lg ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <PriceDisplay price={item.price} />
                  </span>
                </>
              ) : (
                <span className="text-2xl font-bold text-pink-500">
                  <PriceDisplay price={item.price} />
                </span>
              )}
            </div>
            
            {/* Description */}
            {item.description && (
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {item.description.length > 150 ? `${item.description.substring(0, 150)}...` : item.description}
              </p>
            )}
            
            {/* Store Info */}
            {storeName && (
              <div className={`flex items-center justify-between py-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                <div className="flex items-center gap-2">
                  <Store className={`h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {storeName}
                  </span>
                </div>
                {storeVerified && (
                  <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                    {isAr ? 'موثق' : 'Verified'}
                  </span>
                )}
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-2 mt-4">
              <Button 
                onClick={() => {
                  const message = isAr 
                    ? `مرحباً، أنا مهتم بالمنتج "${productName}" المعروض بسعر ${item.salePrice || item.price} درهم`
                    : `Hi, I'm interested in "${productName}" listed at ${item.salePrice || item.price} AED`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="w-full py-5 text-sm font-semibold rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white"
              >
                <MessageCircle className="h-4 w-4 me-2" />
                {isAr ? 'تواصل واتساب' : 'WhatsApp'}
              </Button>
              
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => onFavoriteToggle && onFavoriteToggle(item)}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Heart className={`h-4 w-4 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleShare}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                {item.store_id && (
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`/store/${item.store_id}`, '_blank')}
                    className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Real Estate Modal - العقارات
  if (itemType === 'real_estate') {
    const propertyImage = item.images?.[0] || item.image;
    const isValidPropertyImage = propertyImage && (propertyImage.startsWith('http') || propertyImage.startsWith('data:') || propertyImage.startsWith('/'));
    const propertyTitle = item.title || item.name || (isAr ? 'عقار' : 'Property');
    const storeName = item.store?.name || item.store_name;
    const storeVerified = item.store?.verified || item.store_verified;
    const isInvestment = item.listingType === 'for_investment' || item.listing_type === 'for_investment';
    const isRent = item.listingType === 'for_rent' || item.listing_type === 'for_rent';
    
    // Listing type colors and labels
    const listingTypeConfig = {
      for_sale: { color: '#10B981', label: isAr ? 'للبيع' : 'For Sale' },
      for_rent: { color: '#3B82F6', label: isAr ? 'للإيجار' : 'For Rent' },
      for_investment: { color: '#F59E0B', label: isAr ? 'للاستثمار' : 'For Investment' }
    };
    const listingType = item.listingType || item.listing_type || 'for_sale';
    const listingConfig = listingTypeConfig[listingType] || listingTypeConfig.for_sale;
    
    // Placeholder images for real estate
    const placeholderImages = [
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80',
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80'
    ];
    const displayImage = isValidPropertyImage ? propertyImage : placeholderImages[Math.floor(Math.random() * placeholderImages.length)];
    
    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div 
          className={`w-full max-w-lg my-2 rounded-2xl overflow-hidden shadow-2xl max-h-[95vh] flex flex-col ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'}`}
          onClick={(e) => e.stopPropagation()}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Property Image */}
          <div className={`relative aspect-[4/3] flex-shrink-0 ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-100'}`}>
            <img 
              src={displayImage} 
              alt={propertyTitle}
              className="w-full h-full object-cover"
            />
            
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-3 end-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Listing Type Badge */}
            <div 
              className="absolute top-3 start-3 text-white text-sm font-bold px-3 py-1.5 rounded-full"
              style={{ backgroundColor: listingConfig.color }}
            >
              {listingConfig.label}
            </div>
            
            {/* Verified Badge */}
            {storeVerified && (
              <div className="absolute bottom-3 end-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {isAr ? 'موثق' : 'Verified'}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Title */}
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {propertyTitle}
            </h2>
            
            {/* Location */}
            {item.location && (
              <div className={`flex items-center gap-2 mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{item.location}</span>
              </div>
            )}
            
            {/* Price Section */}
            <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-white/5' : 'bg-purple-50'}`}>
              <div className="text-2xl font-bold" style={{ color: listingConfig.color }}>
                <PriceDisplay price={item.salePrice || item.price} />
                {isRent && (
                  <span className="text-lg font-normal opacity-70">/{isAr ? 'سنة' : 'year'}</span>
                )}
              </div>
              {item.salePrice && item.price && item.salePrice < item.price && (
                <div className={`mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <span className="line-through text-sm"><PriceDisplay price={item.price} /></span>
                </div>
              )}
            </div>
            
            {/* Specs Grid */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {item.bedrooms && (
                <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.bedrooms}</div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{isAr ? 'غرف نوم' : 'Beds'}</div>
                </div>
              )}
              {item.bathrooms && (
                <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.bathrooms}</div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{isAr ? 'حمامات' : 'Baths'}</div>
                </div>
              )}
              {item.area && (
                <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.area}</div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{isAr ? 'قدم مربع' : 'sqft'}</div>
                </div>
              )}
            </div>
            
            {/* Investment Details */}
            {isInvestment && (item.expected_roi || item.rental_yield || item.occupancy_rate) && (
              <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="h-4 w-4 text-amber-500" />
                  <span className={`font-semibold text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                    {isAr ? 'تفاصيل الاستثمار' : 'Investment Details'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {item.expected_roi && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-white'}`}>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{isAr ? 'العائد المتوقع' : 'Expected ROI'}</div>
                      <div className={`text-sm font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{item.expected_roi}%</div>
                    </div>
                  )}
                  {item.rental_yield && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-white'}`}>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{isAr ? 'الإيجار السنوي' : 'Annual Rent'}</div>
                      <div className={`text-sm font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}><PriceDisplay price={item.rental_yield} /></div>
                    </div>
                  )}
                  {item.occupancy_rate && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-white'}`}>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{isAr ? 'نسبة الإشغال' : 'Occupancy'}</div>
                      <div className={`text-sm font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{item.occupancy_rate}%</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Description */}
            {item.description && (
              <div className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <p className="text-sm leading-relaxed">{item.description}</p>
              </div>
            )}
            
            {/* Store Info */}
            {storeName && (
              <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {isAr ? 'المعلن' : 'Advertiser'}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {item.store?.logo || item.store_logo ? (
                      <img src={item.store?.logo || item.store_logo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
                        <Store className="h-5 w-5" />
                      </div>
                    )}
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{storeName}</span>
                  </div>
                  {storeVerified && (
                    <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                      <Shield className="h-3 w-3" />
                      {isAr ? 'موثق' : 'Verified'}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={() => {
                    const priceText = `${(item.salePrice || item.price)?.toLocaleString()} ${isAr ? 'درهم' : 'AED'}`;
                    const message = isAr 
                      ? `مرحباً، أنا مهتم بالعقار "${propertyTitle}" في ${item.location || 'الإمارات'} المعروض بسعر ${priceText}`
                      : `Hi, I'm interested in "${propertyTitle}" in ${item.location || 'UAE'} listed at ${priceText}`;
                    const phone = item.store?.whatsapp || item.store?.phone || item.store_phone || '';
                    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                  className="py-5 text-sm font-semibold rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white"
                >
                  <MessageCircle className="h-4 w-4 me-2" />
                  {isAr ? 'واتساب' : 'WhatsApp'}
                </Button>
                <Button 
                  onClick={() => {
                    const phone = item.store?.phone || item.store_phone || '';
                    if (phone) window.open(`tel:${phone}`, '_self');
                  }}
                  variant="outline"
                  className={`py-5 text-sm font-semibold rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Phone className="h-4 w-4 me-2" />
                  {isAr ? 'اتصال' : 'Call'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline"
                  onClick={handleShare}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Share2 className="h-4 w-4 me-2" />
                  {isAr ? 'مشاركة' : 'Share'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => onFavoriteToggle && onFavoriteToggle(item)}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Heart className={`h-4 w-4 me-2 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                  {isAr ? 'المفضلة' : 'Favorite'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Car Plate Modal
  if (itemType === 'car_plate') {
    return (
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className={`w-full max-w-xs rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'}`}
          onClick={(e) => e.stopPropagation()}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className={`relative p-5 ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
            <button 
              onClick={onClose}
              className={`absolute top-3 end-3 p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Emirate Badge */}
            <div className="flex justify-center mb-4">
              <span 
                className="text-sm font-bold px-4 py-1.5 rounded-full text-white"
                style={{ backgroundColor: emirateColor }}
              >
                {item.emirate || 'Dubai'}
              </span>
            </div>

            {/* UAE Plate Design (official) */}
            <div className="flex justify-center">
              <PlateVisual
                emirate={item.emirate}
                category={item.plateType}
                code={item.plateCode}
                number={getDisplayPlateNumber(item) || item.plateNumber}
                width={280}
                showCodeOnPlate={item.show_on_plate}
              />
            </div>

            {/* Badges */}
            <div className="flex items-center justify-center gap-2 mt-3">
              {(item.negotiable === true || item.negotiable === 'true' || item.negotiable === 'yes') && !isSold && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
                  {isAr ? 'قابل للتفاوض' : 'Negotiable'}
                </span>
              )}
            </div>
          </div>

          {/* Price Section */}
          <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {isAr ? 'السعر' : 'Price'}
              </span>
              <span className="text-2xl font-bold" style={{ color: emirateColor }}>
                <PriceDisplay price={item.price} />
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="px-5 py-3 space-y-2">
            <div className={`flex items-center justify-between py-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 opacity-40" />
                <span className="text-sm">{isAr ? 'الإمارة' : 'Emirate'}</span>
              </div>
              <span className="text-sm font-medium">{item.emirate || 'Dubai'}</span>
            </div>

            <div className={`flex items-center justify-between py-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 opacity-40" />
                <span className="text-sm">{isAr ? 'النوع' : 'Type'}</span>
              </div>
              <span className="text-sm font-medium">{item.plateType || (isAr ? 'خصوصي' : 'Private')}</span>
            </div>

            {/* Store Info */}
            {item.store_name && (
              <div className={`pt-3 mt-2 border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {isAr ? 'المعلن' : 'Advertiser'}
                </div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Store className={`h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {item.store_name}
                    </span>
                    <StoreBadges owner={item.store_owner} size="sm" />
                  </div>
                  {item.store_verified && <Shield className="h-4 w-4 text-[#1E9BD9]" />}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {!isSold && (
            <div className="px-5 pb-5 pt-2 space-y-2">
              <Button 
                onClick={handleWhatsApp}
                className="w-full py-5 text-sm font-semibold rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white"
              >
                <MessageCircle className="h-4 w-4 me-2" />
                {isAr ? 'تواصل واتساب' : 'WhatsApp'}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline"
                  onClick={handleShare}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Share2 className="h-4 w-4 me-1" />
                  {isAr ? 'مشاركة' : 'Share'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleCopyNumber}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  {copied ? <Check className="h-4 w-4 me-1 text-emerald-500" /> : <Copy className="h-4 w-4 me-1" />}
                  {isAr ? 'نسخ' : 'Copy'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Car Modal - Larger with scroll and image gallery
  if (itemType === 'car') {
    const carImages = item.images?.length > 0 ? item.images : (item.image ? [item.image] : []);
    const placeholderImages = [
      'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800&q=80',
      'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&q=80',
      'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800&q=80'
    ];
    const displayImages = carImages.length > 0 ? carImages : [placeholderImages[0]];
    
    const CONDITIONS = {
      new: { ar: 'جديد', en: 'New', color: '#10B981', bg: 'bg-emerald-500/15' },
      used: { ar: 'مستعمل', en: 'Used', color: '#F59E0B', bg: 'bg-amber-500/15' }
    };
    const condition = CONDITIONS[item.condition?.toLowerCase()] || CONDITIONS.used;
    
    const COLORS_AR = {
      white: 'أبيض', black: 'أسود', silver: 'فضي', gray: 'رمادي', grey: 'رمادي',
      red: 'أحمر', blue: 'أزرق', green: 'أخضر', gold: 'ذهبي', brown: 'بني',
      beige: 'بيج', orange: 'برتقالي', yellow: 'أصفر', purple: 'بنفسجي', pink: 'وردي'
    };
    const colorAr = COLORS_AR[item.color?.toLowerCase()] || item.color;
    
    const currentImage = displayImages[carImageIndex] || placeholderImages[0];
    const isValidImage = currentImage && (currentImage.startsWith('http') || currentImage.startsWith('data:') || currentImage.startsWith('/'));
    
    const nextImage = () => setCarImageIndex((prev) => (prev + 1) % displayImages.length);
    const prevImage = () => setCarImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    
    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div 
          className={`w-full max-w-lg my-2 rounded-2xl overflow-hidden shadow-2xl max-h-[95vh] flex flex-col ${
            isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Car Image with Gallery */}
          <div className={`relative aspect-[4/3] flex-shrink-0 ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-100'}`}>
            <img 
              src={isValidImage ? currentImage : placeholderImages[0]} 
              alt={`${item.make} ${item.model}`}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = placeholderImages[0]; }}
            />
            
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-3 end-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Image Navigation - Fixed for RTL/LTR */}
            {displayImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); isAr ? nextImage() : prevImage(); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); isAr ? prevImage() : nextImage(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter Badge */}
                <div className="absolute top-3 start-3 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                  {carImageIndex + 1} / {displayImages.length}
                </div>
                
                {/* Image Dots - Moved higher */}
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
                  {displayImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setCarImageIndex(idx); }}
                      className={`w-2 h-2 rounded-full transition-colors ${idx === carImageIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
            
            {/* Badges */}
            <div className="absolute top-3 start-3 flex gap-2">
              {displayImages.length <= 1 && (
                <>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${condition.bg}`} style={{ color: condition.color }}>
                    {isAr ? condition.ar : condition.en}
                  </span>
                  {item.year && (
                    <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white">
                      {item.year}
                    </span>
                  )}
                </>
              )}
            </div>
            
            {/* Condition & Year Badge (when gallery exists - move to bottom left) */}
            {displayImages.length > 1 && (
              <div className="absolute bottom-3 start-3 flex gap-2">
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${condition.bg}`} style={{ color: condition.color }}>
                  {isAr ? condition.ar : condition.en}
                </span>
                {item.year && (
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white">
                    {item.year}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Car Info */}
            <div className="p-5">
              {/* Title & Price */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {item.make} {item.model}
                </h2>
                {item.year && displayImages.length <= 1 && (
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.year}</span>
                )}
              </div>
              
              {/* Price Section */}
              <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-white/5' : 'bg-blue-50'}`}>
                {item.hasDiscount && item.salePrice ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-blue-500 font-bold text-2xl">
                      <PriceDisplay price={item.salePrice} />
                    </span>
                    <span className={`line-through text-base ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      <PriceDisplay price={item.price} />
                    </span>
                    <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      -{Math.round(item.discountPercentage || ((item.price - item.salePrice) / item.price * 100))}%
                    </span>
                  </div>
                ) : (
                  <span className="text-blue-500 font-bold text-2xl">
                    <PriceDisplay price={item.price} />
                  </span>
                )}
              </div>
              
              {/* Description if exists */}
              {item.description && (
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {item.description}
                </p>
              )}
              
              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Year */}
                {item.year && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'السنة' : 'Year'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.year}
                    </div>
                  </div>
                )}
                
                {item.mileage !== undefined && item.mileage !== null && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'المسافة المقطوعة' : 'Mileage'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.mileage?.toLocaleString()} {isAr ? 'كم' : 'km'}
                    </div>
                  </div>
                )}
                
                {item.color && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'اللون' : 'Color'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {isAr ? colorAr : item.color}
                    </div>
                  </div>
                )}
                
                {item.origin && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'المنشأ' : 'Origin'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.origin === 'gcc' ? (isAr ? 'خليجي' : 'GCC') : (isAr ? 'مستورد' : 'Imported')}
                    </div>
                  </div>
                )}
                
                {item.cylinders && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'السلندرات' : 'Cylinders'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.cylinders}
                    </div>
                  </div>
                )}
                
                {item.doors && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'الأبواب' : 'Doors'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.doors}
                    </div>
                  </div>
                )}
                
                {item.condition && (
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isAr ? 'الحالة' : 'Condition'}
                    </div>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.condition === 'new' ? (isAr ? 'جديد' : 'New') : (isAr ? 'مستعمل' : 'Used')}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Store Info with seller type badge */}
              {item.store_name && (
                <div className={`pt-4 mt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {isAr ? 'المعلن' : 'Advertiser'}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {item.store_logo ? (
                        <img src={item.store_logo} alt={item.store_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                          <Store className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {item.store_name}
                          </span>
                          {/* Seller type badge - متجر/شخصي */}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            item.seller_type === 'personal' 
                              ? isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                              : isDarkMode ? 'bg-[#1E9BD9]/20 text-[#1E9BD9]' : 'bg-[#1E9BD9]/10 text-[#1E9BD9]'
                          }`}>
                            {item.seller_type === 'personal' ? (isAr ? 'شخصي' : 'Personal') : (isAr ? 'متجر' : 'Store')}
                          </span>
                        </div>
                        {item.store_verified && (
                          <div className="flex items-center gap-1 text-xs text-[#1E9BD9]">
                            <Shield className="h-3 w-3" />
                            {isAr ? 'موثق' : 'Verified'}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Visit Store Button - only for store type */}
                    {item.seller_type !== 'personal' && item.store_id && (
                      <Link 
                        to={`/stores/${item.store_id}`}
                        className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                          isDarkMode 
                            ? 'bg-[#1E9BD9]/20 text-[#1E9BD9] hover:bg-[#1E9BD9]/30' 
                            : 'bg-[#1E9BD9]/10 text-[#1E9BD9] hover:bg-[#1E9BD9]/20'
                        }`}
                      >
                        {isAr ? 'زيارة المتجر' : 'Visit Store'}
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Actions - Fixed at bottom */}
          {!isSold && (
            <div className={`flex-shrink-0 px-5 pb-5 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
              <Button 
                onClick={handleWhatsApp}
                className="w-full py-5 text-sm font-semibold rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white mb-2"
              >
                <MessageCircle className="h-5 w-5 me-2" />
                {isAr ? 'تواصل واتساب' : 'WhatsApp'}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline"
                  onClick={handleShare}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Share2 className="h-4 w-4 me-1" />
                  {isAr ? 'مشاركة' : 'Share'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => onFavoriteToggle?.(item.id)}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <Heart className={`h-4 w-4 me-1 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                  {isAr ? 'المفضلة' : 'Favorite'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Phone Number Modal (default)
  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-xs rounded-2xl overflow-hidden shadow-2xl ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* Header - Clean with Logo */}
        <div className={`relative p-5 ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
          {/* Close Button */}
          <button 
            onClick={onClose}
            className={`absolute top-3 end-3 p-1.5 rounded-full transition-colors ${
              isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Status Badge */}
          {isSold && (
            <div className="absolute top-3 start-3">
              <span className="text-[10px] font-bold px-2 py-1 rounded bg-red-500 text-white">
                {isAr ? 'مباع' : 'SOLD'}
              </span>
            </div>
          )}

          {/* Operator Logo - Centered */}
          <div className="flex justify-center mb-4">
            <OperatorLogo
              src={op.logo}
              name={isAr ? op.nameAr : op.nameEn}
              accent={op.accent}
              className="w-16 h-16"
              letterClass="text-2xl"
              testId="modal-operator-logo"
            />
          </div>

          {/* Number with Copy Button */}
          <div className="flex items-center justify-center gap-2">
            <PhoneNumberLockup
              value={getDisplayNumber(item)}
              size="md"
              tone={isDarkMode ? 'light' : 'dark'}
              testId="modal-phone-number"
            />
            {!isNumberMasked(item) && (
              <button 
                onClick={handleCopyNumber}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                }`}
              >
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </button>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {item.category === 'vip' && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-500/20 text-amber-500">
                VIP
              </span>
            )}
            {(item.negotiable === true || item.negotiable === 'true') && !isSold && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
                {isAr ? 'قابل للتفاوض' : 'Negotiable'}
              </span>
            )}
          </div>
        </div>

        {/* Price Section */}
        <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isAr ? 'السعر' : 'Price'}
            </span>
            {/* Handle discount display for personal items */}
            {(item.hasDiscount || (item.original_price && item.original_price > item.price)) && !isSold ? (
              <div className="text-end">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-red-500">
                    <PriceDisplay price={item.salePrice || item.price} />
                  </span>
                  {(item.discountPercentage || item.discount_percentage) && (
                    <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      -{Math.round(item.discountPercentage || item.discount_percentage)}%
                    </span>
                  )}
                </div>
                <span className="text-gray-400 line-through text-sm">
                  <PriceDisplay price={item.original_price || (item.hasDiscount ? item.price : null)} />
                </span>
              </div>
            ) : (
              <span 
                className={`text-2xl font-bold ${isSold ? 'text-gray-500 line-through' : ''}`} 
                style={{ color: isSold ? '' : op.accent }}
              >
                <PriceDisplay price={item.price} />
              </span>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-3 space-y-2">
          {/* Operator */}
          <div className={`flex items-center justify-between py-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 opacity-40" />
              <span className="text-sm">{isAr ? 'المشغل' : 'Operator'}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="modal-operator-name">
              <OperatorLogo src={op.logo} name={isAr ? op.nameAr : op.nameEn} accent={op.accent} className="w-5 h-5" letterClass="text-[10px]" />
              <span className="text-sm font-medium">
                {(isAr ? op.nameAr : op.nameEn) || '—'}
              </span>
            </div>
          </div>

          {/* Negotiable */}
          <div className={`flex items-center justify-between py-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 opacity-40" />
              <span className="text-sm">{isAr ? 'التفاوض' : 'Negotiation'}</span>
            </div>
            <span className={`text-sm font-medium ${(item.negotiable === true || item.negotiable === 'true') ? 'text-emerald-500' : 'text-gray-500'}`}>
              {(item.negotiable === true || item.negotiable === 'true') 
                ? (isAr ? 'متاح' : 'Available') 
                : (isAr ? 'غير متاح' : 'Not available')
              }
            </span>
          </div>

          {/* Advertiser/Seller Info */}
          {item.store_name && (
            <div className={`pt-3 mt-2 border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
              {/* Section Title */}
              <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {isAr ? 'المعلن' : 'Advertiser'}
              </div>
              
              {/* Type + Name */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Store className={`h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <div>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {item.store_name}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    item.seller_type === 'personal'
                      ? isDarkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-500'
                      : isDarkMode ? 'bg-[#1E9BD9]/20 text-[#1E9BD9]' : 'bg-[#1E9BD9]/10 text-[#1E9BD9]'
                  }`}>
                    {item.seller_type === 'personal'
                      ? (isAr ? 'شخصي' : 'Personal')
                      : (isAr ? 'متجر' : 'Store')
                    }
                  </span>
                  {item.store_verified && <Shield className="h-4 w-4 text-[#1E9BD9]" />}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isSold && (
          <div className="px-5 pb-5 pt-2 space-y-2">
            {/* WhatsApp Button */}
            <Button 
              onClick={handleWhatsApp}
              className="w-full py-5 text-sm font-semibold rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white"
            >
              <MessageCircle className="h-4 w-4 me-2" />
              {isAr ? 'تواصل واتساب' : 'WhatsApp'}
            </Button>

            {/* Secondary Actions */}
            <div className="grid grid-cols-3 gap-2">
              <Button 
                variant="outline" 
                onClick={() => onFavoriteToggle && onFavoriteToggle(item)}
                className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
              >
                <Heart className={`h-4 w-4 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
              <Button 
                variant="outline"
                onClick={handleShare}
                className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
              >
                <Share2 className="h-4 w-4" />
              </Button>
              {item.store_id && (
                <Button 
                  variant="outline"
                  onClick={() => window.open(`/store/${item.store_id}`, '_blank')}
                  className={`py-4 rounded-xl ${isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}`}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Sold State */}
        {isSold && (
          <div className="px-5 pb-5 pt-2">
            <div className={`text-center py-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {isAr ? 'هذا الرقم تم بيعه' : 'This number has been sold'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
